"use client";

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { fetchPublicReport } from "@marketing/ai-credit-report/lib/api";

export interface GeneratedReportData {
  id: string;
  publicToken: string;
  status: string;
  domain: string;
  consistencyScore?: number | null;
  visibilitySummary?: any;
  purchaseId?: string | null;
  errorMessage?: string | null;
  // Progress tracking fields
  progressStep?: string | null;
  progressPercent?: number | null;
  progressMessage?: string | null;
}

/**
 * Shared post-submit flow for the public report generate forms (brand and
 * person): poll the public report by token every 2s while it's pending or
 * running, and redirect to the report page once it completes.
 *
 * The relative `./reports/…` push works from any page directly under
 * `[locale]` — both marketing form pages are.
 */
export function useReportGenerationFlow() {
  const [publicToken, setPublicToken] = useState<string | null>(null);
  const router = useRouter();

  const { data: reportResponse } = useQuery({
    queryKey: ["ai-credit-report", publicToken],
    queryFn: async () => {
      if (!publicToken) return null;
      return fetchPublicReport(publicToken);
    },
    enabled: Boolean(publicToken),
    refetchInterval: (query) => {
      const r = query.state.data as any;
      const status = r?.report?.status;
      // Only poll while pending or running
      return (status === "pending" || status === "running") ? 2000 : false;
    },
  });

  const report: GeneratedReportData | null = reportResponse?.report ?? null;

  // Redirect to report page only when it is fully complete
  useEffect(() => {
    if (!report) return;

    if (report.status === "complete") {
      router.push(`./reports/${report.publicToken}`);
    }
  }, [report, router]);

  // Report is actively generating → callers swap the form for the
  // ReportGeneratingPanel
  const isActivelyGenerating = Boolean(report &&
    (report.status === "pending" ||
     report.status === "running" ||
     report.status === "partial" ||
     report.status === "processing_discovery"));

  // Submit buttons stay disabled while the pipeline runs
  const isReportGenerating = Boolean(report &&
    (report.status === "pending" ||
     report.status === "running" ||
     report.status === "processing_discovery"));

  return {
    publicToken,
    setPublicToken,
    report,
    isActivelyGenerating,
    isReportGenerating,
  };
}
